export interface SyncProgress {
  bookmarkedCases: string[];
  bookmarkedNodes: string[];
  masteredNodes: string[];
  notes: Record<string, string>;
  noteUpdatedAt: Record<string, string>;
  recentCases: string[];
  viewedAt: Record<string, string>;
  viewedCases: string[];
  openedCases: string[];
  completedSteps: string[];
  theme: 'light' | 'dark';
  preferencesUpdatedAt: string;
}

export interface SyncSource {
  bookmarkedCases: string[];
  bookmarkedNodes: string[];
  masteredNodes: string[];
  notes: Record<string, string>;
  noteUpdatedAt: Record<string, string>;
  recentCases: string[];
  viewedAt: Record<string, string>;
  viewedCases: string[];
  openedCases: string[];
  completedSteps: string[];
  theme: 'light' | 'dark';
  preferencesUpdatedAt: string;
}

function unique(local: string[], remote: string[], max: number): string[] {
  return [...new Set([...local, ...remote])].slice(0, max);
}

function latestDates(local: Record<string, string>, remote: Record<string, string>): Record<string, string> {
  const output: Record<string, string> = {};
  for (const [key, value] of Object.entries({ ...local, ...remote })) {
    const left = Date.parse(local[key] || '');
    const right = Date.parse(remote[key] || '');
    const chosen = right > left ? remote[key] : local[key] ?? remote[key];
    if (chosen && !Number.isNaN(Date.parse(chosen))) output[key] = chosen;
  }
  return output;
}

export function toSyncProgress(state: SyncSource): SyncProgress {
  return {
    bookmarkedCases: state.bookmarkedCases,
    bookmarkedNodes: state.bookmarkedNodes,
    masteredNodes: state.masteredNodes,
    notes: state.notes,
    noteUpdatedAt: state.noteUpdatedAt,
    recentCases: state.recentCases,
    viewedAt: state.viewedAt,
    viewedCases: state.viewedCases,
    openedCases: state.openedCases,
    completedSteps: state.completedSteps,
    theme: state.theme,
    preferencesUpdatedAt: state.preferencesUpdatedAt,
  };
}

export function mergeProgress(local: SyncProgress, remote: SyncProgress): SyncProgress {
  const noteUpdatedAt = latestDates(local.noteUpdatedAt, remote.noteUpdatedAt);
  const notes: Record<string, string> = {};
  for (const key of new Set([...Object.keys(local.notes), ...Object.keys(remote.notes)])) {
    const localValue = local.notes[key];
    const remoteValue = remote.notes[key];
    if (localValue === undefined) {
      if (remoteValue !== undefined) notes[key] = remoteValue;
      continue;
    }
    if (remoteValue === undefined) {
      notes[key] = localValue;
      continue;
    }
    const localTime = Date.parse(local.noteUpdatedAt[key] || '') || 0;
    const remoteTime = Date.parse(remote.noteUpdatedAt[key] || '') || 0;
    notes[key] = remoteTime > localTime ? remoteValue : localValue;
  }
  const localPreference = Date.parse(local.preferencesUpdatedAt || '') || 0;
  const remotePreference = Date.parse(remote.preferencesUpdatedAt || '') || 0;
  const theme = remotePreference > localPreference ? remote.theme : local.theme;
  return {
    bookmarkedCases: unique(local.bookmarkedCases, remote.bookmarkedCases, 500),
    bookmarkedNodes: unique(local.bookmarkedNodes, remote.bookmarkedNodes, 500),
    masteredNodes: unique(local.masteredNodes, remote.masteredNodes, 500),
    notes,
    noteUpdatedAt,
    recentCases: unique(local.recentCases, remote.recentCases, 30),
    viewedAt: latestDates(local.viewedAt, remote.viewedAt),
    viewedCases: unique(local.viewedCases, remote.viewedCases, 500),
    openedCases: unique(local.openedCases, remote.openedCases, 80),
    completedSteps: unique(local.completedSteps, remote.completedSteps, 500),
    theme,
    preferencesUpdatedAt: remotePreference > localPreference ? remote.preferencesUpdatedAt : local.preferencesUpdatedAt,
  };
}

export function progressPayload(state: SyncProgress): string {
  return JSON.stringify(state);
}

function sameArray(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((value, index) => value === b[index]);
}

function sameMap(left: Record<string, string>, right: Record<string, string>): boolean {
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((key) => left[key] === right[key]);
}

export function sameProgress(left: SyncProgress, right: SyncProgress): boolean {
  return (
    sameArray(left.bookmarkedCases, right.bookmarkedCases) &&
    sameArray(left.bookmarkedNodes, right.bookmarkedNodes) &&
    sameArray(left.masteredNodes, right.masteredNodes) &&
    sameArray(left.recentCases, right.recentCases) &&
    sameArray(left.viewedCases, right.viewedCases) &&
    sameArray(left.openedCases, right.openedCases) &&
    sameArray(left.completedSteps, right.completedSteps) &&
    sameMap(left.notes, right.notes) &&
    sameMap(left.noteUpdatedAt, right.noteUpdatedAt) &&
    sameMap(left.viewedAt, right.viewedAt) &&
    left.theme === right.theme &&
    left.preferencesUpdatedAt === right.preferencesUpdatedAt
  );
}
